/**
 * ECGWaveformGenerator — v10.3.0
 *
 * Genera muestras del ECG (12 derivaciones simplificadas → II) a partir de:
 *   ecg.rhythm      (ritmo_ecg)
 *   heart_rate      (fc)
 *   ecg.amplitude   (ecg_amplitude)  — escala la magnitud, NO el baseline
 *   ecg.regularity  (implícito por rhythm)
 *   ecg.qrs_width   (implícito por rhythm)
 *   noise_level / artifact_type / signal_quality  — vía artifacts.js
 *
 * Salida: valor en el rango [-60, +60] (unidades de canvas). El baseline
 * siempre está en 0 y el llamador lo suma al centro del eje Y del canvas.
 */
import { sampleArtifact } from './artifacts';
import { FLATLINE_RHYTHMS } from './engine';

// --- Componentes elementales del complejo cardíaco ------------------------

function pWave(ph, start, width, amp = 8) {
  if (ph < start || ph > start + width) return 0;
  return Math.sin(((ph - start) / width) * Math.PI) * amp;
}
function qrsNarrow(ph, start, amp = 42) {
  const d = ph - start;
  if (d < 0 || d > 0.07) return 0;
  if (d < 0.015) return -6 * (d / 0.015);
  if (d < 0.035) return -6 + (amp + 6) * ((d - 0.015) / 0.02);
  if (d < 0.055) return amp - (amp + 12) * ((d - 0.035) / 0.02);
  return -12 + 12 * ((d - 0.055) / 0.015);
}
function qrsWide(ph, start, amp = 38) {
  const d = ph - start;
  if (d < 0 || d > 0.18) return 0;
  return Math.sin((d / 0.18) * Math.PI) * amp * (d < 0.09 ? 1 : -0.85);
}
function tWave(ph, start, width = 0.16, amp = 11) {
  if (ph < start || ph > start + width) return 0;
  return Math.sin(((ph - start) / width) * Math.PI) * amp;
}
function sinusBeat(ph, { pr = 0.16, qrsAmp = 42, showP = true, tAmp = 11 }) {
  let v = 0;
  if (showP) v += pWave(ph, 0.02, 0.09);
  v += qrsNarrow(ph, 0.02 + pr, qrsAmp);
  v += tWave(ph, 0.02 + pr + 0.12, 0.16, tAmp);
  return v;
}

// Jitter determinista por latido para ritmos irregulares
function beatJitter(beatIndex, amount) {
  const x = Math.sin(beatIndex * 127.1 + 311.7) * 43758.5453;
  return ((x - Math.floor(x)) - 0.5) * 2 * amount;
}

// --- Morfología por ritmo -------------------------------------------------

function beatValue(t, rate, rhythm) {
  const base = 0; // baseline SIEMPRE en 0
  switch (rhythm) {
    case 'asistolia':
      return base;

    case 'fibrilacion_ventricular': {
      // Caótico, sin QRS organizado. Amplitud media-alta.
      const v = Math.sin(t * 0.021) * 18 + Math.sin(t * 0.037 + 1.3) * 14 +
        Math.sin(t * 0.011 + 4.1) * 10 + Math.sin(t * 0.055 + 2.2) * 8;
      return base + v;
    }

    case 'flutter_auricular': {
      const fPeriod = 200;
      const saw = ((t % fPeriod) / fPeriod) * 12 - 6;
      const beatPeriod = 60000 / rate;
      const ph = (t % beatPeriod) / beatPeriod;
      return base + saw + qrsNarrow(ph, 0.05) + tWave(ph, 0.28, 0.12, 6);
    }

    case 'fibrilacion_auricular':
    case 'taquicardia_irregular_angosto': {
      const meanPeriod = 60000 / rate;
      let acc = 0, idx = 0;
      const tt = t % (meanPeriod * 8);
      for (let i = 0; i < 16; i++) {
        const p = meanPeriod * (1 + beatJitter(i, 0.28));
        if (tt < acc + p) { idx = i; break; }
        acc += p;
      }
      const period = meanPeriod * (1 + beatJitter(idx, 0.28));
      const ph = (tt - acc) / period;
      const fib = Math.sin(t * 0.045) * 2.5 + Math.sin(t * 0.09 + 1) * 1.8;
      return base + fib + qrsNarrow(ph, 0.04) + tWave(ph, 0.3, 0.14, 8);
    }

    case 'taquicardia_ventricular': {
      const beatPeriod = 60000 / Math.max(rate, 100);
      const ph = (t % beatPeriod) / beatPeriod;
      return base + qrsWide(ph, 0.05, 40) + qrsWide(ph, 0.55, -18);
    }

    case 'aesp': {
      // Actividad organizada lenta (sin pulso: la Pleth es plana)
      const beatPeriod = 60000 / Math.max(30, Math.min(rate, 80));
      const ph = (t % beatPeriod) / beatPeriod;
      return base + sinusBeat(ph, { qrsAmp: 26, tAmp: 6 });
    }

    case 'bloqueo_av_1': {
      const beatPeriod = 60000 / rate;
      const ph = (t % beatPeriod) / beatPeriod;
      return base + sinusBeat(ph, { pr: 0.28 });
    }

    case 'bloqueo_av_2_mobitz1': {
      const beatPeriod = 60000 / rate;
      const cycle = Math.floor(t / beatPeriod) % 4;
      const ph = (t % beatPeriod) / beatPeriod;
      if (cycle === 3) return base + pWave(ph, 0.02, 0.09);
      const pr = 0.14 + cycle * 0.06;
      return base + sinusBeat(ph, { pr });
    }

    case 'bloqueo_av_2_mobitz2': {
      const beatPeriod = 60000 / rate;
      const cycle = Math.floor(t / beatPeriod) % 3;
      const ph = (t % beatPeriod) / beatPeriod;
      if (cycle === 2) return base + pWave(ph, 0.02, 0.09);
      return base + sinusBeat(ph, { pr: 0.16 });
    }

    case 'bloqueo_av_completo': {
      const pPeriod = 60000 / 90;
      const vPeriod = 60000 / Math.max(25, Math.min(rate, 55));
      const pPh = (t % pPeriod) / pPeriod;
      const vPh = (t % vPeriod) / vPeriod;
      return base + pWave(pPh, 0.05, 0.12) + qrsWide(vPh, 0.1, 34);
    }

    case 'tsv':
    case 'taquicardia_regular_angosto': {
      const beatPeriod = 60000 / rate;
      const ph = (t % beatPeriod) / beatPeriod;
      return base + qrsNarrow(ph, 0.06) + tWave(ph, 0.42, 0.18, 7);
    }

    case 'taquicardia_sinusal':
    case 'bradicardia_sinusal':
    case 'sinusal':
    default: {
      const beatPeriod = 60000 / rate;
      const ph = (t % beatPeriod) / beatPeriod;
      return base + sinusBeat(ph, { pr: 0.16 });
    }
  }
}

/**
 * Muestrea el ECG en `timeMs`. Devuelve valor en unidades de canvas
 * (rango típico -20..+50) ANTES de traslación al centro del eje Y.
 *
 * @param {number} timeMs   — reloj compartido (performance.now())
 * @param {object} cfg      — salida de resolveSignalConfig()
 */
export function sampleECG(timeMs, cfg) {
  const { rhythm, hr, ecgAmp, noise } = cfg;
  const rate = Math.max(20, Math.min(300, hr || 70));

  // Si el ritmo no tiene actividad, sólo se ve ruido (útil para asistolia
  // con detección de "línea plana con artefactos" del monitor real).
  if (FLATLINE_RHYTHMS.has(rhythm)) {
    return sampleArtifact(timeMs, cfg._vitals || {}, 'ecg').offset * 0.6;
  }

  const raw = beatValue(timeMs, rate, rhythm);
  const scaled = raw * ecgAmp * (noise?.signalAttenuation ?? 1);
  const { offset } = sampleArtifact(timeMs, cfg._vitals || {}, 'ecg');
  return scaled + offset;
}
